import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Printer, ArrowRight } from "lucide-react";
import { storeApi, formatPrice } from "@/lib/storefront";
import SEO from "@/components/storefront/SEO";
import { BRAND } from "@/lib/brand";

export default function LineSheet() {
  const [items, setItems] = useState([]);
  const [cats, setCats] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      storeApi.get("/products", { params: { per_page: 100 } }),
      storeApi.get("/categories"),
    ])
      .then(([p, c]) => {
        setItems(p.data.items || []);
        const map = {};
        (c.data || []).forEach((x) => { map[x.id] = x.name; });
        setCats(map);
      })
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="mx-auto max-w-[1100px] px-6 py-14 md:py-20" data-testid="line-sheet-page">
      <SEO
        title="Wholesale Line Sheet"
        description={`Current ${BRAND.name} assortment with SKUs and MSRP — for buyers at quilt shops, sewing studios, and longarm businesses. Trade pricing shared upon account approval.`}
        noindex
      />
      <div className="flex flex-wrap items-end justify-between gap-4 print:hidden">
        <div>
          <div className="mb-3 text-xs uppercase tracking-[0.3em] text-[#6B5158]">Wholesale · B2B</div>
          <h1 className="font-display text-4xl leading-tight md:text-[56px]">
            Line <span className="font-display-soft italic">sheet</span>
          </h1>
          <p className="mt-4 max-w-2xl text-base leading-relaxed text-[#3A2E33]">
            Our current assortment at a glance — SKUs, categories, and MSRP. Trade pricing and minimums are shared upon
            account approval. Items marked Coming Soon are inbound and ship as soon as they land.
          </p>
        </div>
        <button
          onClick={() => window.print()}
          className="inline-flex items-center gap-2 rounded-full border border-[#2B2127] px-6 py-3 text-sm text-[#2B2127] hover:bg-[#2B2127] hover:text-white"
          data-testid="print-line-sheet"
        >
          <Printer size={15} /> Print / save PDF
        </button>
      </div>

      {/* Print header */}
      <div className="hidden print:block">
        <div className="font-display text-2xl">{BRAND.name} — Wholesale Line Sheet</div>
        <div className="text-xs text-[#6B5158]">{BRAND.wholesaleEmail} · {BRAND.addressLines[0]}, {BRAND.addressLines[1]}</div>
      </div>

      <div className="mt-8 overflow-x-auto rounded-md border border-[#E6D4CC] bg-white print:border-0">
        <table className="w-full text-left text-[14px]">
          <thead>
            <tr className="border-b border-[#E6D4CC] bg-[#F9F1EB] text-[11px] uppercase tracking-[0.18em] text-[#6B5158]">
              <th className="px-4 py-3">Item</th>
              <th className="px-4 py-3">SKU</th>
              <th className="px-4 py-3">Category</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3 text-right">MSRP</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#E6D4CC]">
            {items.map((p) => (
              <tr key={p.id} className="align-middle hover:bg-[#FAF6F1]" data-testid={`ls-row-${p.sku}`}>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    {p.primary_image && <img src={p.primary_image} alt="" className="h-10 w-10 rounded object-cover print:hidden" loading="lazy" />}
                    <Link to={`/p/${p.seo_slug || p.id}`} className="font-medium text-[#2B2127] hover:text-[#9D5C6C]">{p.title}</Link>
                  </div>
                </td>
                <td className="whitespace-nowrap px-4 py-3 font-mono text-[12px] text-[#6B5158]">{p.sku || "—"}</td>
                <td className="whitespace-nowrap px-4 py-3 text-[13px] text-[#3A2E33]">{cats[p.category_id] || "—"}</td>
                <td className="whitespace-nowrap px-4 py-3">
                  {p.coming_soon
                    ? <span className="rounded-full bg-[#6D2E4B] px-2.5 py-0.5 text-[10px] uppercase tracking-[0.12em] text-white">Coming soon</span>
                    : <span className="rounded-full bg-emerald-100 px-2.5 py-0.5 text-[10px] uppercase tracking-[0.12em] text-emerald-800">In stock</span>}
                </td>
                <td className="whitespace-nowrap px-4 py-3 text-right font-semibold text-[#2B2127] store-nums">{formatPrice(p.price, p.currency)}</td>
              </tr>
            ))}
            {!loading && items.length === 0 && (
              <tr><td colSpan={5} className="px-4 py-10 text-center text-sm text-[#6B5158]">Catalog is being stocked — check back shortly.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="mt-8 rounded-md border border-[#E6D4CC] bg-[#F1E3DC] p-6 print:hidden">
        <div className="text-[14px] leading-relaxed text-[#3A2E33]">
          <span className="font-semibold">Ready to order for your shop?</span> Approved wholesale accounts receive trade
          pricing (−25% off MSRP), fair small-batch minimums, and a direct line to our team.
        </div>
        <div className="mt-4 flex flex-wrap items-center gap-4">
          <Link to="/wholesale" className="inline-flex items-center gap-2 rounded-full bg-[#6D2E4B] px-6 py-3 text-sm text-white hover:bg-[#59253d]" data-testid="ls-apply-cta">
            Apply for a wholesale account <ArrowRight size={14} />
          </Link>
          <a href={`mailto:${BRAND.wholesaleEmail}`} className="text-sm text-[#2B2127] underline decoration-[#9D5C6C] underline-offset-4">{BRAND.wholesaleEmail}</a>
        </div>
      </div>
    </div>
  );
}
